import React, { useMemo } from "react";
import classNames from "classnames";
import { SubscriptionPlan } from "enums/subscription-plans";
import {
  STRIPE_PRICE_METADATA_TYPE_PREMIUM,
  STRIPE_PRICE_METADATA_TYPE_DEEP,
  STRIPE_PRICE_METADATA_TYPE_TEAM,
} from "constants/config";
import PricingCard from "../PricingCard";
import LoadingPricingCard from "../LoadingPricingCard/LoadingPricingCard";
import { getSubscriptionPlanLabel } from "../../utils";
import type {
  StripeProduct,
  StripePrice,
  StripeProductData,
} from "../../types";

interface PricingTableProps {
  products?: StripeProduct[] | null;
  isLoading?: boolean;
  plans: SubscriptionPlan[];
  currentPlan?: SubscriptionPlan;
  isAnnualSelected: boolean;
  isSignedIn?: boolean;
  onCardClick: (price?: StripePrice, product?: StripeProductData) => void;
  onFreeClick?: () => void;
  className?: string;
  containerClassName?: string;
  cardClassName?: string;
}

/**
 * PricingTable - A reusable component for rendering pricing cards
 *
 * Features:
 * - Supports different plan combinations (individual, team, etc.)
 * - Handles loading states
 * - Filters and sorts products automatically
 * - Manages pricing card layout and styling
 */
const PricingTable: React.FC<PricingTableProps> = ({
  products,
  isLoading = false,
  plans,
  currentPlan,
  isAnnualSelected,
  isSignedIn = false,
  onCardClick,
  onFreeClick,
  className,
  containerClassName,
  cardClassName = "sm:not-first:pl-6 sm:not-last:border-r sm:not-last:pr-6 sm:flex-1 border-b border-border-base py-6",
}) => {
  // Filter products based on requested plans
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    return products.filter((product) => {
      const tier = product.product_data.metadata?.tier?.toLowerCase();
      return plans.some(
        (plan) =>
          (plan === SubscriptionPlan.PREMIUM &&
            tier === SubscriptionPlan.PREMIUM.toLowerCase()) ||
          (plan === SubscriptionPlan.DEEP &&
            tier === SubscriptionPlan.DEEP.toLowerCase()) ||
          (plan === SubscriptionPlan.TEAM &&
            tier === SubscriptionPlan.TEAM.toLowerCase())
      );
    });
  }, [products, plans]);

  // Sort products by plan order
  const sortedProducts = useMemo(() => {
    const tierOrder: Record<string, number> = {
      [SubscriptionPlan.PREMIUM.toLowerCase()]: 1,
      [SubscriptionPlan.DEEP.toLowerCase()]: 2,
      [SubscriptionPlan.TEAM.toLowerCase()]: 3,
    };

    return filteredProducts.slice().sort((a, b) => {
      const aTier = a.product_data.metadata?.tier?.toLowerCase() || "";
      const bTier = b.product_data.metadata?.tier?.toLowerCase() || "";
      return (tierOrder[aTier] || 999) - (tierOrder[bTier] || 999);
    });
  }, [filteredProducts]);

  // Determine container class based on number of cards
  const numCards =
    (plans.includes(SubscriptionPlan.FREE) ? 1 : 0) +
    sortedProducts.length +
    (plans.includes(SubscriptionPlan.ENTERPRISE) ? 1 : 0);

  // For 3+ cards, ensure minimum width to prevent wrapping
  // max-w-page (850px) minus horizontal padding (48px) allows 3 cards to display properly side by side
  const defaultContainerClassName =
    numCards > 2
      ? "flex flex-col gap-6 sm:flex-row sm:gap-0 border-t border-border-base sm:min-w-[calc(var(--max-w-page)-var(--cdl-spacing-x-lg)*2)]"
      : "flex flex-col sm:flex-row sm:gap-0 border-t border-border-base";

  // Render loading state
  if (isLoading) {
    return (
      <div
        className={classNames(
          containerClassName || defaultContainerClassName,
          className
        )}
      >
        {Array.from({ length: numCards }).map((_, i) => (
          <LoadingPricingCard
            key={i}
            className={cardClassName
              .replace("border-b", "")
              .replace("py-6", "")}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        containerClassName || defaultContainerClassName,
        className
      )}
    >
      {/* Free plan card */}
      {plans.includes(SubscriptionPlan.FREE) && (
        <PricingCard
          className={cardClassName}
          onClick={onFreeClick || (() => {})}
          title={getSubscriptionPlanLabel(SubscriptionPlan.FREE)}
          plan={SubscriptionPlan.FREE.toLowerCase()}
          currentPlan={currentPlan}
          isAnnualSelected={isAnnualSelected}
          isSignedIn={isSignedIn}
        />
      )}

      {/* Product cards (Pro, Deep, Team) */}
      {sortedProducts.map((product) => {
        const { product_data } = product;
        let price;

        const priceMetadataType =
          product_data.metadata?.tier?.toLowerCase() ===
          SubscriptionPlan.TEAM.toLowerCase()
            ? STRIPE_PRICE_METADATA_TYPE_TEAM
            : product_data.metadata?.tier?.toLowerCase() ===
                SubscriptionPlan.DEEP.toLowerCase()
              ? STRIPE_PRICE_METADATA_TYPE_DEEP
              : STRIPE_PRICE_METADATA_TYPE_PREMIUM;

        if (!isAnnualSelected) {
          price = product_data.prices.find(
            (priceItem) =>
              priceItem.recurring !== null &&
              priceItem.recurring.interval === "month" &&
              priceItem.metadata.type == priceMetadataType
          );
        } else {
          price = product_data.prices.find(
            (priceItem) =>
              priceItem.recurring !== null &&
              priceItem.recurring.interval === "year" &&
              priceItem.metadata.type == priceMetadataType
          );
        }

        return price ? (
          <PricingCard
            key={`${product_data.id}_${price.id}`}
            className={cardClassName}
            onClick={onCardClick}
            title={product_data.name}
            plan={product_data.metadata?.tier?.toLowerCase() ?? ""}
            product={product_data}
            price={price}
            currentPlan={currentPlan}
            isAnnualSelected={isAnnualSelected}
            isSignedIn={isSignedIn}
          />
        ) : null;
      })}

      {/* Enterprise plan card */}
      {plans.includes(SubscriptionPlan.ENTERPRISE) && (
        <PricingCard
          className={cardClassName}
          onClick={() => {}}
          title={getSubscriptionPlanLabel(SubscriptionPlan.ENTERPRISE)}
          plan={SubscriptionPlan.ENTERPRISE.toLowerCase()}
          currentPlan={currentPlan}
          isAnnualSelected={isAnnualSelected}
          isSignedIn={isSignedIn}
        />
      )}
    </div>
  );
};

export default PricingTable;
